import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { components } from "react-select";
import { MotifIcon } from "@ey-xd/motif-react";
import {
  IconoirNavArrowDown,
  IconoirNavArrowUp,
  IconoirXmark,
} from "@ey-xd/motif-icon";
import "./MultiSelect.scss";

interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
  customStyles?: any;
  hasSelectAll?: boolean;
  selectAllLabel?: string;
  selectAllValue?: string;
  theme?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "All",
  isDisabled = false,
  customStyles = {},
  hasSelectAll = true,
  selectAllLabel = "All",
  selectAllValue = "all",
  theme = "ch-dark",
}) => {
  const selectContainerRef = useRef<HTMLDivElement>(null);
  const [selectWidth, setSelectWidth] = useState(0);

  useEffect(() => {
    const container = selectContainerRef.current;

    if (!container) {
      return;
    }

    const updateWidth = () => {
      setSelectWidth(container.clientWidth);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  // Add "Select All" option if enabled
  const optionsWithSelectAll = useMemo(
    () =>
      hasSelectAll
        ? [{ value: selectAllValue, label: selectAllLabel }, ...options]
        : options,
    [hasSelectAll, selectAllValue, selectAllLabel, options],
  );

  const selectedDisplayValues = useMemo(
    () =>
      optionsWithSelectAll.filter(
        (opt) => value.includes(opt.value) && opt.value !== selectAllValue,
      ),
    [optionsWithSelectAll, selectAllValue, value],
  );

  const visibleValueCount = useMemo(() => {
    if (selectedDisplayValues.length === 0) {
      return 0;
    }

    if (selectedDisplayValues.length === 1 || selectWidth <= 0) {
      return 1;
    }

    const measureText = (text: string, font: string) => {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (!context) {
        return text.length * 8;
      }

      context.font = font;
      return context.measureText(text).width;
    };

    const badgeFont = "400 12px EYInterstate";
    const countFont = "400 12px EYInterstate";
    const chipPadding = 28;
    const countPadding = 16;
    const minCountWidth = 34;
    const countMarginLeft = 4;
    const controlReserveWidth = value.length > 0 ? 110 : 72;
    const availableWidth = Math.max(selectWidth - controlReserveWidth, 0);

    if (availableWidth <= 0) {
      return 1;
    }

    const getChipWidth = (label: string) =>
      Math.ceil(measureText(label, badgeFont) + chipPadding);

    const getCountWidth = (hiddenCount: number) =>
      Math.max(
        minCountWidth,
        Math.ceil(measureText(`+${hiddenCount}`, countFont) + countPadding),
      );

    let usedWidth = getChipWidth(selectedDisplayValues[0].label);
    let visibleCount = 1;

    for (let index = 1; index < selectedDisplayValues.length; index += 1) {
      const nextChipWidth = getChipWidth(selectedDisplayValues[index].label);
      const hiddenCount = selectedDisplayValues.length - (index + 1);
      const remainingWidth =
        hiddenCount > 0 ? getCountWidth(hiddenCount) + countMarginLeft : 0;

      if (usedWidth + nextChipWidth + remainingWidth > availableWidth) {
        break;
      }

      usedWidth += nextChipWidth;
      visibleCount += 1;
    }

    return Math.max(1, visibleCount);
  }, [selectWidth, selectedDisplayValues, value.length]);

  const hiddenValueCount = Math.max(
    selectedDisplayValues.length - visibleValueCount,
    0,
  );

  // Memoize CustomOption to prevent recreation
  const CustomOption = useMemo(
    () => (props: any) => {
      const { data, isSelected, innerRef, innerProps } = props;
      const isSelectAllOption = data.value === selectAllValue;
      const currentValue = value;
      const isIndeterminate =
        isSelectAllOption &&
        currentValue.length > 0 &&
        currentValue.length < options.length;

      return (
        <components.Option
          {...props}
          ref={innerRef}
          {...innerProps}
          className="menu-popup"
        >
          <span className="checkbox-wrapper">
            <input
              type="checkbox"
              checked={isSelected}
              ref={(input) => {
                if (input && isIndeterminate) {
                  input.indeterminate = true;
                }
              }}
              onChange={() => {}}
              readOnly
              className="menu-item-checkbox cursor-pointer"
            />
          </span>
          <span className="menu-item-label cursor-pointer">{data.label}</span>
        </components.Option>
      );
    },
    [selectAllValue, value, options.length],
  );

  // Memoize DropdownIndicator
  const DropdownIndicator = useMemo(
    () => (props: any) => {
      return (
        <components.DropdownIndicator {...props}>
          <MotifIcon
            iconFunction={
              props.selectProps.menuIsOpen
                ? IconoirNavArrowUp
                : IconoirNavArrowDown
            }
            size="20"
            fill="#A4A3B1"
            className="cursor-pointer"
          />
        </components.DropdownIndicator>
      );
    },
    [],
  );

  // Memoize ClearIndicator
  const ClearIndicator = useMemo(
    () => (props: any) => {
      return (
        <components.ClearIndicator {...props}>
          <MotifIcon iconFunction={IconoirXmark} size="20" fill="#A4A3B1" />
        </components.ClearIndicator>
      );
    },
    [],
  );

  // Memoize CustomMultiValue
  const CustomMultiValue = useMemo(
    () => (props: any) => {
      if (props.data.value === selectAllValue) {
        return null;
      }

      const actualIndex = selectedDisplayValues.findIndex(
        (v: any) => v.value === props.data.value,
      );

      if (actualIndex > -1 && actualIndex < visibleValueCount) {
        return <components.MultiValue {...props} />;
      }

      if (actualIndex === visibleValueCount && hiddenValueCount > 0) {
        return (
          <div className={`${theme}__remainingCount`}>+{hiddenValueCount}</div>
        );
      }

      return null;
    },
    [
      hiddenValueCount,
      selectAllValue,
      selectedDisplayValues,
      theme,
      visibleValueCount,
    ],
  );

  const handleChange = (selected: any) => {
    const selectedValues = selected
      ? selected.map((opt: any) => opt.value)
      : [];

    if (!hasSelectAll) {
      onChange(selectedValues);
      return;
    }

    const hasAll = selectedValues.includes(selectAllValue);
    const prevHasAll = value.includes(selectAllValue);
    const allOtherOptions = options.map((opt) => opt.value);

    let newSelection: string[] = [];

    if (hasAll && !prevHasAll) {
      newSelection = [selectAllValue, ...allOtherOptions];
    } else if (!hasAll && prevHasAll) {
      newSelection = [];
    } else if (hasAll) {
      newSelection = selectedValues.filter(
        (val: string) => val !== selectAllValue,
      );
    } else {
      newSelection = selectedValues;
      if (newSelection.length === allOtherOptions.length) {
        newSelection = [selectAllValue, ...newSelection];
      }
    }
    onChange(newSelection);
  };

  let defaultStyles = {};

  if (theme == "ch-dark") {
    defaultStyles = {
      control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: "#23232f",
        borderColor: state.isFocused ? "#85B9FD" : "#4E4E5D",
        boxShadow: state.isFocused ? "0 0 0 1px #85B9FD" : provided.boxShadow,
        "&:hover": {
          borderColor: state.isFocused ? "#85B9FD" : "#4E4E5D",
          backgroundColor: "#2E2E38",
          color: "#DEDEE2",
          borderWidth: state.isFocused ? "2px" : "1px",
          borderStyle: "solid",
          outlineColor: state.isFocused ? "#85B9FD" : "#4E4E5D",
          outlineStyle: "solid",
          outlineWidth: state.isFocused ? "2px" : "1px",
        },
      }),
      indicatorSeparator: () => ({
        display: "none",
      }),
      clearIndicator: (provided: any) => ({
        ...provided,
        color: "#A4A3B1",
        padding: "8px 0px 8px 8px",
        cursor: "pointer",
      }),
      placeholder: (provided: any) => ({
        ...provided,
        color: "#A4A3B1",
        fontFamily: "EYInterstate",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: "300",
        lineHeight: "20px",
      }),
      menu: (provided: any) => ({
        ...provided,
        backgroundColor: "#23232f",
        zIndex: 99,
      }),
      menuList: (provided: any) => ({
        ...provided,
        backgroundColor: "#23232f",
      }),
      option: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isSelected ? "#33333E" : "#23232F",
        color: "#DEDEE2",
        fontFamily: "EYInterstate",
        fontStyle: "normal",
        fontWeight: "300",
        fontSize: "16px",
        lineHeight: "24px",
        "&:hover": {
          backgroundColor: "#2E2E38",
          color: "#DEDEE2",
        },
      }),
      multiValue: (provided: any) => ({
        ...provided,
        backgroundColor: "#23232F",
        borderRadius: "2px",
        border: "1px solid #4E4E5D",
      }),
      multiValueLabel: (provided: any) => ({
        ...provided,
        color: "#DEDEE2",
        fontSize: "12px",
        padding: "3px 4px 3px 8px",
        fontFamily: "EYInterstate",
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "18px",
      }),
      multiValueRemove: (provided: any) => ({
        ...provided,
        color: "#A4A3B1",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "transparent",
          color: "#A4A3B1",
        },
      }),
      valueContainer: (provided: any) => ({
        ...provided,
        flexWrap: "nowrap",
        overflow: "hidden",
      }),
      ...customStyles,
    };
  } else {
    defaultStyles = {
      control: (provided: any) => ({
        ...provided,
        backgroundColor: "#FFFFFF",
        borderColor: "#C3C3CB",
        "&:hover": {
          borderColor: "#C3C3CB",
          backgroundColor: "#F3F3F5",
          color: "#2E2E38",
          borderWidth: "1px",
          borderStyle: "solid",
          outlineColor: "#85B9FD",
          outlineStyle: "solid",
          outlineWidth: "2px",
        },
      }),
      indicatorSeparator: () => ({
        display: "none",
      }),
      clearIndicator: (provided: any) => ({
        ...provided,
        color: "#737387",
        padding: "8px 0px 8px 8px",
        cursor: "pointer",
      }),
      menu: (provided: any) => ({
        ...provided,
        backgroundColor: "#fff",
      }),
      menuList: (provided: any) => ({
        ...provided,
        backgroundColor: "#fff",
      }),
      option: (provided: any) => ({
        ...provided,
        backgroundColor: "#fff",
        color: "#2E2E38",
        fontFamily: "EYInterstate",
        fontStyle: "normal",
        fontWeight: "300",
        fontSize: "14px",
        lineHeight: "23.996px",
        "&:hover": {
          backgroundColor: "#E6E6E9",
          color: "#E6E6E9",
        },
      }),
      multiValue: (provided: any) => ({
        ...provided,
        color: "#2E2E38",
        backgroundColor: "#E6E6E9",
        borderRadius: "2px",
        border: "1px solid #E6E6E9",
      }),

      multiValueLabel: (provided: any) => ({
        ...provided,
        color: "#2E2E38",
        fontSize: "11.998px",
        padding: "4px 8px",
        fontFamily: "EYInterstate",
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "18.004px",
      }),
      multiValueRemove: (provided: any) => ({
        ...provided,
        color: "#A4A3B1",
        cursor: "pointer",
        "&:hover": {
          color: "#23232f",
        },
      }),
      valueContainer: (provided: any) => ({
        ...provided,
        flexWrap: "nowrap",
        overflow: "hidden",
      }),
      ...customStyles,
    };
  }

  const displayValue = useMemo(
    () => optionsWithSelectAll.filter((opt) => value.includes(opt.value)),
    [optionsWithSelectAll, value],
  );

  // Memoize components object to prevent recreation
  const selectComponents = useMemo(
    () => ({
      Option: CustomOption,
      MultiValue: CustomMultiValue,
      DropdownIndicator,
      ClearIndicator,
    }),
    [CustomOption, CustomMultiValue, DropdownIndicator, ClearIndicator],
  );

  return (
    <div ref={selectContainerRef}>
      <Select
        classNamePrefix={theme}
        value={displayValue}
        onChange={handleChange}
        options={optionsWithSelectAll}
        isMulti
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isSearchable={false}
        isDisabled={isDisabled}
        placeholder={placeholder}
        components={selectComponents}
        styles={defaultStyles}
      />
    </div>
  );
};

export default MultiSelect;
